"use client";

import { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";

const URL_RE = /^https?:\/\/\S+$/i;
const MIN_TEXT = 30;
const MIN_URL = 10;

type SubmitPayload = {
  value: string;
  method: "url" | "text";
};

type JdSearchModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: SubmitPayload) => void;
  onTrack: (eventName: string, params?: Record<string, string | number | boolean | undefined>) => void;
};

export function JdSearchModal({ open, onClose, onSubmit, onTrack }: JdSearchModalProps) {
  const [jd, setJd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputStartedRef = useRef(false);
  const urlDetectedRef = useRef(false);

  const trimmed = jd.trim();
  const isUrl = URL_RE.test(trimmed);
  const valid = isUrl ? trimmed.length >= MIN_URL : trimmed.length >= MIN_TEXT;

  useEffect(() => {
    if (open) requestAnimationFrame(() => textareaRef.current?.focus());
  }, [open]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(Math.max(textarea.scrollHeight, 64), 320)}px`;
  }, [jd]);

  function handleChange(next: string) {
    setJd(next);

    if (!inputStartedRef.current && next.length > 0) {
      inputStartedRef.current = true;
      onTrack("jd_input_started");
    }

    const looksUrl = URL_RE.test(next.trim());
    if (looksUrl && !urlDetectedRef.current) {
      urlDetectedRef.current = true;
      onTrack("jd_url_detected", { url: next.trim() });
    } else if (!looksUrl && urlDetectedRef.current) {
      urlDetectedRef.current = false;
    }
  }

  function handleSubmit() {
    if (!valid || submitting) return;

    setSubmitting(true);
    onSubmit({ value: trimmed, method: isUrl ? "url" : "text" });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="JD로 검색"
      subtitle="채용 공고 링크나 본문을 붙여넣으면 그대로 평가 요건을 뽑아드립니다."
    >
      <div
        className={`trial-glass-subtle rounded-[22px] p-2 transition-shadow duration-200 ${
          valid ? "ring-2 ring-[var(--trial-brand-soft)]" : ""
        }`}
      >
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={jd}
            onChange={(event) => handleChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="JD 링크나 본문을 알려주세요"
            rows={1}
            className="w-full resize-none bg-transparent px-4 py-3.5 pr-14 text-[15px] leading-relaxed text-[var(--trial-foreground)] placeholder:text-[var(--trial-muted)]/70 focus:outline-none tablet:text-[16px]"
            style={{ minHeight: "56px" }}
          />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!valid || submitting}
            aria-label="평가 요건 추출"
            className="trial-brand-gradient absolute right-2.5 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full text-white shadow-[0_4px_16px_-2px_rgba(79,109,245,0.5)] transition-all duration-200 enabled:hover:scale-[1.04] enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-30"
          >
            {submitting ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-center gap-1.5 text-[12px] text-[var(--trial-muted)]">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-[var(--trial-success)]"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        가입 없이 · 무료 · 약 30초
      </div>
    </Modal>
  );
}
